import type { SandboxStatus } from "@/lib/session-queue-ui-state"

export interface ConnectionFlowRuntimeSnapshot {
  status: string
  statusObservedAt: number | null
  sandboxStatus: SandboxStatus
  sandboxProviderId: string | null
  sandboxDashboardUrl: string | null
  doSessionId: string | null
  doSandboxId: string | null
  doOpenCodeSessionId: string | null
  eventCount: number | null
  connections: number | null
  history: ConnectionFlowHistoryDebugSnapshot | null
  replication: ConnectionFlowReplicationDebugSnapshot | null
  runtimeLifecycle: ConnectionFlowRuntimeLifecycleDebugSnapshot | null
}

export type ConnectionFlowObservedRuntimeReachability =
  | "reachable"
  | "unroutable"
  | "stopped"
  | "unreachable"
  | "missing"
  | "indeterminate"

export interface ConnectionFlowObservedRuntimeStateRecord {
  providerId: string | null
  runtimeId: string | null
  runtimeGeneration: number | null
  providerReachability: ConnectionFlowObservedRuntimeReachability | null
  observedAt: number | null
  source: string | null
  errorCode: string | null
  message: string | null
}

export interface ConnectionFlowRuntimeObservationProbeRecord {
  providerId: string | null
  runtimeId: string | null
  runtimeGeneration: number | null
  observedAt: number | null
  source: string | null
  outcome: string | null
  errorCode: string | null
  message: string | null
}

export interface ConnectionFlowRuntimeObservationDebugSnapshot {
  observedRuntimeState: ConnectionFlowObservedRuntimeStateRecord | null
  brokerHealthProbe: ConnectionFlowRuntimeObservationProbeRecord | null
  unknownBrokerProbe: ConnectionFlowRuntimeObservationProbeRecord | null
  terminalControlPlaneProbe: ConnectionFlowRuntimeObservationProbeRecord | null
  exactRuntimeLookup: ConnectionFlowRuntimeObservationProbeRecord | null
}

export interface ConnectionFlowDegradedAttachDebugSnapshot {
  active: boolean
  attemptCount: number
  firstAt: number | null
  nextForcedEscalationAt: number | null
  forcedEscalationDue: boolean
  thresholdExceededAt: number | null
}

export interface ConnectionFlowSseReconnectDebugSnapshot {
  transportActive: boolean
  attemptCount: number
  failureStartedAt: number | null
  nextForcedEscalationAt: number | null
  forcedEscalationDue: boolean
}

export interface ConnectionFlowRecoveryDecisionDebugSnapshot {
  kind: "replace_runtime" | "replacement_handoff_blocked"
  source:
    | "exact_runtime_lookup"
    | "alarm_broker_repair"
    | "sse_reconnect"
    | "recovered_runtime_broker"
    | "recovered_runtime_control_probe"
  providerTruth: "unroutable"
  reasonCode: string
  summary: string
}

export interface ConnectionFlowRuntimeLifecycleDebugSnapshot {
  runtimeDesiredState?: string | null
  runtimeState?: string | null
  lastRecoveryAction: string | null
  lastRecoveryReason: string | null
  recoveryPhase: string | null
  recoveryStartedAt: number | null
  recoveryBlockReason: string | null
  degradedAttach: ConnectionFlowDegradedAttachDebugSnapshot | null
  sseReconnect: ConnectionFlowSseReconnectDebugSnapshot | null
  recoveryDecision: ConnectionFlowRecoveryDecisionDebugSnapshot | null
  observation: ConnectionFlowRuntimeObservationDebugSnapshot | null
}

export interface ConnectionFlowAvailabilityStatus {
  level: "warning" | "blocked"
  label: string
  detail: string | null
}

export interface ConnectionFlowHistoryDebugSnapshot {
  historyContinuity: "contiguous" | "broken" | null
  historyToken: string | null
  tokenVersion: string | null
  headCursor: number | null
  tailCursor: number | null
  lineageField: string | null
  browserHistoryEpoch: number | null
  baselineFingerprint: string | null
}

export interface ConnectionFlowReplicationDebugSnapshot {
  journalEpoch: string | null
  browserHistoryEpoch: number | null
  replicationContinuity: "contiguous" | "broken" | null
  lastCommittedSourceSeq: number | null
  nextExpectedSourceSeq: number | null
  authoritativeReady: boolean | null
  mode: string | null
}

export interface ConnectionFlowLinkConfig {
  sandboxDashboardUrl: string | null
  cloudflareAccountId: string | null
  cloudflareSessionDoNamespaceId: string | null
  neonTablesUrl: string | null
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeSandboxStatus(value: unknown): SandboxStatus {
  switch (value) {
    case "none":
    case "creating":
    case "starting_services":
    case "starting_agent":
    case "starting_opencode":
    case "connecting_sse":
    case "running":
    case "stopped":
    case "error":
      return value
    default:
      return "none"
  }
}

function normalizeHistoryContinuity(value: unknown): "contiguous" | "broken" | null {
  return value === "contiguous" || value === "broken" ? value : null
}

function normalizeNonNegativeNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : null
}

function normalizeBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null
}

function normalizeObservedRuntimeReachability(
  value: unknown,
): ConnectionFlowObservedRuntimeReachability | null {
  switch (value) {
    case "reachable":
    case "unroutable":
    case "stopped":
    case "unreachable":
    case "missing":
    case "indeterminate":
      return value
    default:
      return null
  }
}

function normalizeConnectionFlowHistoryDebugSnapshot(
  raw: unknown,
): ConnectionFlowHistoryDebugSnapshot | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>
  const rawSync = row.sync
  const sync = rawSync && typeof rawSync === "object" && !Array.isArray(rawSync)
    ? rawSync as Record<string, unknown>
    : null

  return {
    historyContinuity: normalizeHistoryContinuity(row.historyContinuity),
    historyToken: asNonEmptyString(sync?.historyToken),
    tokenVersion: asNonEmptyString(sync?.tokenVersion),
    headCursor: normalizeNonNegativeNumber(sync?.headCursor),
    tailCursor: normalizeNonNegativeNumber(sync?.tailCursor),
    lineageField: asNonEmptyString(sync?.lineageField),
    browserHistoryEpoch: normalizeNonNegativeNumber(sync?.browserHistoryEpoch),
    baselineFingerprint: asNonEmptyString(sync?.baselineFingerprint),
  }
}

function normalizeConnectionFlowReplicationDebugSnapshot(
  raw: unknown,
): ConnectionFlowReplicationDebugSnapshot | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>

  return {
    journalEpoch: asNonEmptyString(row.journalEpoch),
    browserHistoryEpoch: normalizeNonNegativeNumber(row.browserHistoryEpoch),
    replicationContinuity: normalizeHistoryContinuity(
      row.replicationContinuity ?? row.historyContinuity,
    ),
    lastCommittedSourceSeq: normalizeNonNegativeNumber(row.lastCommittedSourceSeq),
    nextExpectedSourceSeq: normalizeNonNegativeNumber(row.nextExpectedSourceSeq),
    authoritativeReady:
      typeof row.authoritativeReady === "boolean" ? row.authoritativeReady : null,
    mode: asNonEmptyString(row.mode),
  }
}

function normalizeConnectionFlowObservedRuntimeStateRecord(
  raw: unknown,
): ConnectionFlowObservedRuntimeStateRecord | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>

  return {
    providerId: asNonEmptyString(row.providerId),
    runtimeId: asNonEmptyString(row.runtimeId),
    runtimeGeneration: normalizeNonNegativeNumber(row.runtimeGeneration),
    providerReachability: normalizeObservedRuntimeReachability(row.providerReachability),
    observedAt: normalizeNonNegativeNumber(row.observedAt),
    source: asNonEmptyString(row.source),
    errorCode: asNonEmptyString(row.errorCode),
    message: asNonEmptyString(row.message),
  }
}

function normalizeConnectionFlowRuntimeObservationProbeRecord(
  raw: unknown,
): ConnectionFlowRuntimeObservationProbeRecord | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>

  return {
    providerId: asNonEmptyString(row.providerId),
    runtimeId: asNonEmptyString(row.runtimeId),
    runtimeGeneration: normalizeNonNegativeNumber(row.runtimeGeneration),
    observedAt: normalizeNonNegativeNumber(row.observedAt),
    source: asNonEmptyString(row.source),
    outcome: asNonEmptyString(row.outcome),
    errorCode: asNonEmptyString(row.errorCode),
    message: asNonEmptyString(row.message),
  }
}

function normalizeConnectionFlowRecoveryDecisionDebugSnapshot(
  raw: unknown,
): ConnectionFlowRecoveryDecisionDebugSnapshot | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>
  const kind = asNonEmptyString(row.kind)
  const source = asNonEmptyString(row.source)
  const providerTruth = asNonEmptyString(row.providerTruth)
  const reasonCode = asNonEmptyString(row.reasonCode)
  const summary = asNonEmptyString(row.summary)

  if (kind !== "replace_runtime" && kind !== "replacement_handoff_blocked") return null
  if (
    source !== "exact_runtime_lookup"
    && source !== "alarm_broker_repair"
    && source !== "sse_reconnect"
    && source !== "recovered_runtime_broker"
    && source !== "recovered_runtime_control_probe"
  ) {
    return null
  }
  if (providerTruth !== "unroutable") return null
  if (!reasonCode || !summary) return null

  return {
    kind,
    source,
    providerTruth,
    reasonCode,
    summary,
  }
}

export function normalizeConnectionFlowRuntimeLifecycleDebugSnapshot(
  raw: unknown,
): ConnectionFlowRuntimeLifecycleDebugSnapshot | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>
  const rawObservation = row.observation
  const observationRow =
    rawObservation && typeof rawObservation === "object" && !Array.isArray(rawObservation)
      ? rawObservation as Record<string, unknown>
      : null

  const observation: ConnectionFlowRuntimeObservationDebugSnapshot | null = observationRow
    ? {
        observedRuntimeState: normalizeConnectionFlowObservedRuntimeStateRecord(
          observationRow.observed_runtime_state,
        ),
        brokerHealthProbe: normalizeConnectionFlowRuntimeObservationProbeRecord(
          observationRow.broker_health_probe,
        ),
        unknownBrokerProbe: normalizeConnectionFlowRuntimeObservationProbeRecord(
          observationRow.unknown_broker_probe,
        ),
        terminalControlPlaneProbe: normalizeConnectionFlowRuntimeObservationProbeRecord(
          observationRow.terminal_control_plane_probe,
        ),
        exactRuntimeLookup: normalizeConnectionFlowRuntimeObservationProbeRecord(
          observationRow.exact_runtime_lookup,
        ),
      }
    : null

  const rawDegradedAttach = row.degradedAttach
  const degradedAttach =
    rawDegradedAttach && typeof rawDegradedAttach === "object" && !Array.isArray(rawDegradedAttach)
      ? {
          active: normalizeBoolean((rawDegradedAttach as Record<string, unknown>).active) ?? false,
          attemptCount:
            normalizeNonNegativeNumber((rawDegradedAttach as Record<string, unknown>).attemptCount) ?? 0,
          firstAt: normalizeNonNegativeNumber((rawDegradedAttach as Record<string, unknown>).firstAt),
          nextForcedEscalationAt: normalizeNonNegativeNumber(
            (rawDegradedAttach as Record<string, unknown>).nextForcedEscalationAt,
          ),
          forcedEscalationDue:
            normalizeBoolean((rawDegradedAttach as Record<string, unknown>).forcedEscalationDue)
            ?? false,
          thresholdExceededAt: normalizeNonNegativeNumber(
            (rawDegradedAttach as Record<string, unknown>).thresholdExceededAt,
          ),
        }
      : null

  const rawSseReconnect = row.sseReconnect
  const sseReconnect =
    rawSseReconnect && typeof rawSseReconnect === "object" && !Array.isArray(rawSseReconnect)
      ? {
          transportActive:
            normalizeBoolean((rawSseReconnect as Record<string, unknown>).transportActive) ?? false,
          attemptCount:
            normalizeNonNegativeNumber((rawSseReconnect as Record<string, unknown>).attemptCount) ?? 0,
          failureStartedAt: normalizeNonNegativeNumber(
            (rawSseReconnect as Record<string, unknown>).failureStartedAt,
          ),
          nextForcedEscalationAt: normalizeNonNegativeNumber(
            (rawSseReconnect as Record<string, unknown>).nextForcedEscalationAt,
          ),
          forcedEscalationDue:
            normalizeBoolean((rawSseReconnect as Record<string, unknown>).forcedEscalationDue)
            ?? false,
        }
      : null

  return {
    runtimeDesiredState: asNonEmptyString(row.runtimeDesiredState),
    runtimeState: asNonEmptyString(row.runtimeState),
    lastRecoveryAction: asNonEmptyString(row.lastRecoveryAction),
    lastRecoveryReason: asNonEmptyString(row.lastRecoveryReason),
    recoveryPhase: asNonEmptyString(row.recoveryPhase),
    recoveryStartedAt: normalizeNonNegativeNumber(row.recoveryStartedAt),
    recoveryBlockReason: asNonEmptyString(row.recoveryBlockReason),
    degradedAttach,
    sseReconnect,
    recoveryDecision: normalizeConnectionFlowRecoveryDecisionDebugSnapshot(row.recoveryDecision),
    observation,
  }
}

export function deriveRuntimeLifecycleAvailabilityStatus(args: {
  runtimeLifecycle: ConnectionFlowRuntimeLifecycleDebugSnapshot | null
}): ConnectionFlowAvailabilityStatus | null {
  const runtimeLifecycle = args.runtimeLifecycle
  if (!runtimeLifecycle) return null

  if (runtimeLifecycle.recoveryDecision?.kind === "replace_runtime") {
    return {
      level: "warning",
      label: "Replacing runtime",
      detail: runtimeLifecycle.recoveryDecision.summary,
    }
  }

  if (runtimeLifecycle.recoveryDecision?.kind === "replacement_handoff_blocked") {
    return {
      level: "blocked",
      label: "Replacement handoff blocked",
      detail: runtimeLifecycle.recoveryDecision.summary,
    }
  }

  if (runtimeLifecycle.recoveryBlockReason) {
    return {
      level: "blocked",
      label: "Recovery blocked",
      detail: `Runtime recovery is blocked: ${runtimeLifecycle.recoveryBlockReason}.`,
    }
  }

  if (runtimeLifecycle.degradedAttach?.forcedEscalationDue) {
    const attemptCount = runtimeLifecycle.degradedAttach.attemptCount
    return {
      level: "warning",
      label: "Escalating sandbox recovery",
      detail:
        attemptCount > 0
          ? `Sandbox auth recovery exceeded its retry window after ${attemptCount} degraded attach failures.`
          : "Sandbox auth recovery exceeded its retry window.",
    }
  }

  if (runtimeLifecycle.degradedAttach?.active) {
    const attemptCount = runtimeLifecycle.degradedAttach.attemptCount
    return {
      level: "warning",
      label: "Recovering sandbox auth",
      detail:
        attemptCount > 0
          ? `Sandbox auth broker is still degraded after ${attemptCount} degraded attach failures.`
          : "Sandbox auth broker is still degraded.",
    }
  }

  const shouldSurfaceSseReconnectStatus = shouldSurfaceRuntimeLifecycleSseReconnectStatus({
    runtimeLifecycle,
  })

  if (shouldSurfaceSseReconnectStatus && runtimeLifecycle.sseReconnect?.forcedEscalationDue) {
    const attemptCount = runtimeLifecycle.sseReconnect.attemptCount
    return {
      level: "warning",
      label: "Escalating runtime recovery",
      detail:
        attemptCount > 0
          ? `The live runtime stream exceeded its reconnect window after ${attemptCount} failures.`
          : "The live runtime stream exceeded its reconnect window.",
    }
  }

  if (shouldSurfaceSseReconnectStatus && runtimeLifecycle.sseReconnect && !runtimeLifecycle.sseReconnect.transportActive) {
    const attemptCount = runtimeLifecycle.sseReconnect.attemptCount
    return {
      level: "warning",
      label: "Restoring runtime stream",
      detail:
        attemptCount > 0
          ? `The live runtime stream is retrying after ${attemptCount} reconnect failures.`
          : "The live runtime stream is reconnecting.",
    }
  }

  const observedRuntimeState = runtimeLifecycle.observation?.observedRuntimeState
  if (
    observedRuntimeState
    && observedRuntimeState.providerReachability
    && observedRuntimeState.providerReachability !== "reachable"
    && observedRuntimeState.providerReachability !== "indeterminate"
    && (
      observedRuntimeState.providerReachability !== "stopped"
      || runtimeLifecycle.runtimeDesiredState === "attached"
    )
  ) {
    return {
      level: "warning",
      label: "Observed runtime degraded",
      detail: `Provider last reported the runtime as ${observedRuntimeState.providerReachability}.`,
    }
  }

  return null
}

function shouldSurfaceRuntimeLifecycleSseReconnectStatus(args: {
  runtimeLifecycle: ConnectionFlowRuntimeLifecycleDebugSnapshot
}): boolean {
  const runtimeLifecycle = args.runtimeLifecycle
  return runtimeLifecycle.runtimeDesiredState !== "detached" &&
    runtimeLifecycle.runtimeState !== "stopped" &&
    runtimeLifecycle.runtimeState !== "none" &&
    runtimeLifecycle.runtimeState !== "creating" &&
    runtimeLifecycle.runtimeState !== "starting"
}

export function deriveRuntimeLifecycleAvailabilityObservedAt(args: {
  runtimeLifecycle: ConnectionFlowRuntimeLifecycleDebugSnapshot | null
}): number | null {
  const runtimeLifecycle = args.runtimeLifecycle
  if (!runtimeLifecycle) return null
  const shouldSurfaceSseReconnectStatus = shouldSurfaceRuntimeLifecycleSseReconnectStatus({
    runtimeLifecycle,
  })

  const candidateTimestamps = [
    runtimeLifecycle.recoveryStartedAt,
    runtimeLifecycle.degradedAttach?.firstAt ?? null,
    runtimeLifecycle.degradedAttach?.thresholdExceededAt ?? null,
    shouldSurfaceSseReconnectStatus ? runtimeLifecycle.sseReconnect?.failureStartedAt ?? null : null,
    runtimeLifecycle.observation?.observedRuntimeState?.observedAt ?? null,
    runtimeLifecycle.observation?.brokerHealthProbe?.observedAt ?? null,
    runtimeLifecycle.observation?.unknownBrokerProbe?.observedAt ?? null,
    runtimeLifecycle.observation?.terminalControlPlaneProbe?.observedAt ?? null,
    runtimeLifecycle.observation?.exactRuntimeLookup?.observedAt ?? null,
  ].filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value > 0)

  if (candidateTimestamps.length === 0) {
    return null
  }

  return Math.max(...candidateTimestamps)
}

export function normalizeConnectionFlowRuntimeSnapshot(
  raw: unknown,
): ConnectionFlowRuntimeSnapshot | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>
  const rawExternalLinks = row.sandboxExternalLinks
  const sandboxExternalLinks =
    rawExternalLinks &&
    typeof rawExternalLinks === "object" &&
    !Array.isArray(rawExternalLinks)
      ? (rawExternalLinks as Record<string, unknown>)
      : null
  return {
    status: asNonEmptyString(row.status) ?? "idle",
    statusObservedAt: normalizeNonNegativeNumber(row.statusObservedAt),
    sandboxStatus: normalizeSandboxStatus(row.sandboxStatus),
    sandboxProviderId: asNonEmptyString(row.sandboxProviderId),
    sandboxDashboardUrl: asNonEmptyString(sandboxExternalLinks?.sandboxDashboardUrl),
    doSessionId: asNonEmptyString(row.doSessionId),
    doSandboxId: asNonEmptyString(row.doSandboxId),
    doOpenCodeSessionId: asNonEmptyString(row.doOpenCodeSessionId),
    eventCount:
      typeof row.eventCount === "number" && Number.isFinite(row.eventCount)
        ? Math.max(0, Math.floor(row.eventCount))
        : null,
    connections:
      typeof row.connections === "number" && Number.isFinite(row.connections)
        ? Math.max(0, Math.floor(row.connections))
        : null,
    history: normalizeConnectionFlowHistoryDebugSnapshot(row.history),
    replication: normalizeConnectionFlowReplicationDebugSnapshot(row.replication),
    runtimeLifecycle: normalizeConnectionFlowRuntimeLifecycleDebugSnapshot(row.runtimeLifecycle),
  }
}

export function normalizeConnectionFlowLinkConfig(
  raw: unknown,
): ConnectionFlowLinkConfig | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>
  return {
    sandboxDashboardUrl: asNonEmptyString(row.sandboxDashboardUrl),
    cloudflareAccountId: asNonEmptyString(row.cloudflareAccountId),
    cloudflareSessionDoNamespaceId: asNonEmptyString(row.cloudflareSessionDoNamespaceId),
    neonTablesUrl: asNonEmptyString(row.neonTablesUrl),
  }
}

export function normalizeExternalUrl(value: string | null): string | null {
  const rawValue = asNonEmptyString(value)
  if (!rawValue) return null

  try {
    const normalizedBaseUrl = /^https?:\/\//.test(rawValue)
      ? rawValue
      : `https://${rawValue}`
    return new URL(normalizedBaseUrl).toString()
  } catch {
    return null
  }
}

export function resolveSandboxDashboardUrl(args: {
  runtimeSnapshot: ConnectionFlowRuntimeSnapshot | null
  connectionFlowLinkConfig: ConnectionFlowLinkConfig | null
  sessionFallbackDashboardUrl?: string | null
}): string | null {
  const runtimeUrl = normalizeExternalUrl(args.runtimeSnapshot?.sandboxDashboardUrl ?? null)
  if (runtimeUrl) return runtimeUrl

  const connectionFlowLinkUrl = normalizeExternalUrl(
    args.connectionFlowLinkConfig?.sandboxDashboardUrl ?? null,
  )
  if (connectionFlowLinkUrl) return connectionFlowLinkUrl

  return normalizeExternalUrl(args.sessionFallbackDashboardUrl ?? null)
}
